import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle, AlertTriangle, ChevronDown, ChevronRight, ChevronsUpDown, Check, Loader2, Link, ArrowRight, Plus, Filter } from "lucide-react";
import { cn } from "@/lib/utils";
import { POLineItem } from "@/hooks/usePOLineItems";
import { useSkuMappings, useAvailableSkus, useCreateSkuMapping } from "@/hooks/useSkuMappings";
import { useCreateProduct } from "@/hooks/useProducts";
import { CreateProductDialog, CreateProductDialogResult } from "./CreateProductDialog";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface POSkuMappingReviewProps {
  purchaseOrderId: string;
  organisationId: string;
  lineItems: POLineItem[];
  userEmail: string;
}

interface SkuGroup {
  sku: string;
  description: string | null;
  isMatched: boolean;
  breakdown?: POLineItem["extraction_source"] extends infer T
  ? T extends { sku_report_breakdown?: infer B }
  ? B
  : never
  : never;
  lineItemIds: string[];
}

const INITIAL_SKU_COUNT = 10;
const LOAD_MORE_COUNT = 20;

function SkuCombobox({
  availableSkus,
  value,
  onChange,
  placeholder = "Select SKU...",
  currentSku,
}: {
  availableSkus: string[];
  value: string | null;
  onChange: (sku: string | null) => void;
  placeholder?: string;
  currentSku?: string;
}) {
  const [open, setOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_SKU_COUNT);
  const [searchValue, setSearchValue] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  const filteredSkus = useMemo(() => {
    let skus = currentSku
      ? availableSkus.filter((s) => s.toLowerCase() !== currentSku.toLowerCase())
      : availableSkus;
    if (searchValue) {
      const lower = searchValue.toLowerCase();
      skus = skus.filter((s) => s.toLowerCase().includes(lower));
    }
    return skus;
  }, [availableSkus, currentSku, searchValue]);

  // Reset visible count when search changes or dropdown opens
  useEffect(() => {
    setVisibleCount(INITIAL_SKU_COUNT);
  }, [searchValue]);

  useEffect(() => {
    if (open) {
      setVisibleCount(INITIAL_SKU_COUNT);
      setSearchValue("");
    }
  }, [open]);

  const visibleSkus = filteredSkus.slice(0, visibleCount);
  const hasMore = visibleCount < filteredSkus.length;

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20 && hasMore) {
      setVisibleCount((prev) => Math.min(prev + LOAD_MORE_COUNT, filteredSkus.length));
    }
  }, [hasMore, filteredSkus.length]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between text-xs h-8"
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search Product..."
            className="h-8 text-xs"
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList ref={listRef} onScroll={handleScroll}>
            <CommandEmpty>No product found.</CommandEmpty>
            <CommandGroup>
              {value && (
                <CommandItem
                  onSelect={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                  className="text-xs text-muted-foreground"
                >
                  Clear selection
                </CommandItem>
              )}
              {visibleSkus.map((sku) => (
                <CommandItem
                  key={sku}
                  value={sku}
                  onSelect={() => {
                    onChange(sku);
                    setOpen(false);
                  }}
                  className="text-xs"
                >
                  <Check
                    className={cn(
                      "mr-1 h-3 w-3",
                      value === sku ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {sku}
                </CommandItem>
              ))}
              {hasMore && (
                <div className="py-1 text-center text-xs text-muted-foreground">
                  Scroll for more ({filteredSkus.length - visibleCount} remaining)
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function POSkuMappingReview({
  purchaseOrderId,
  organisationId,
  lineItems,
  userEmail,
}: POSkuMappingReviewProps) {
  const [selectedSourceType, setSelectedSourceType] = useState<string>("all");

  // Fetch org connections + clone metadata to build source options
  const [orgConnections, setOrgConnections] = useState<Record<string, any> | null>(null);
  const [inventorySnapshotsEnabled, setInventorySnapshotsEnabled] = useState(false);
  const [productIdentifier, setProductIdentifier] = useState<string>("SKU");
  const [sourceOrganisationId, setSourceOrganisationId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchOrgData = async () => {
      const { data } = await (supabase as any)
        .from("organisations")
        .select("connections, inventory_snapshots_enabled, product_identifier, is_test_clone, source_organisation_id")
        .eq("id", organisationId)
        .single();
      if (data) {
        setOrgConnections((data.connections as Record<string, any>) || {});
        setInventorySnapshotsEnabled(!!data.inventory_snapshots_enabled);
        setProductIdentifier(data.product_identifier ?? "SKU");
        if (data.is_test_clone && data.source_organisation_id) {
          setSourceOrganisationId(data.source_organisation_id);
        }
      }
    };
    if (organisationId) fetchOrgData();
  }, [organisationId]);

  const { data: existingMappings } = useSkuMappings(organisationId, sourceOrganisationId);
  const { data: availableSkus = [] } = useAvailableSkus(organisationId, selectedSourceType === "all" ? undefined : selectedSourceType, sourceOrganisationId);
  const createMapping = useCreateSkuMapping();

  const derivedSourceOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [
      { value: "all", label: "All Sources" },
    ];
    if (!orgConnections) return options;

    const connectionTypes = new Set<string>();
    Object.values(orgConnections).forEach((conn: any) => {
      const type = conn.type?.toLowerCase() || "";
      if (type.includes("csv") && conn.import_type === "ecommerce") {
        connectionTypes.add("amazon_csv");
      } else if (type.includes("shopify")) {
        connectionTypes.add("shopify");
      } else if (type.includes("amazon")) {
        connectionTypes.add("amazon");
      }
    });

    if (connectionTypes.has("amazon")) options.push({ value: "amazon", label: "Amazon" });
    if (connectionTypes.has("shopify")) options.push({ value: "shopify", label: "Shopify" });
    if (connectionTypes.has("amazon_csv")) options.push({ value: "amazon_csv", label: "Amazon CSV Import" });
    if (inventorySnapshotsEnabled) options.push({ value: "snapshots", label: "Inventory Snapshots" });

    return options;
  }, [orgConnections, inventorySnapshotsEnabled]);
  const createProduct = useCreateProduct();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Local state for mapping changes: aliasSku -> selectedPrimarySku
  const [mappingChanges, setMappingChanges] = useState<Record<string, string | null>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [creatingProduct, setCreatingProduct] = useState<string | null>(null);
  const [createDialogData, setCreateDialogData] = useState<{ sku: string; description: string | null } | null>(null);

  // Build a set of known SKUs from existingMappings + availableSkus (case-insensitive)
  const knownSkuSet = useMemo(() => {
    const set = new Set<string>();
    existingMappings?.forEach((m) => {
      set.add(m.primary_value.toLowerCase().trim());
      set.add(m.alias_value.toLowerCase().trim());
    });
    availableSkus.forEach((s) => set.add(s.toLowerCase().trim()));
    return set;
  }, [existingMappings, availableSkus]);

  // Group line items by unique SKU
  const skuGroups = useMemo(() => {
    const groups = new Map<string, SkuGroup>();
    lineItems.forEach((item) => {
      const sku = item.sku?.trim() || "";
      if (!sku) return; // Skip empty SKUs

      if (!groups.has(sku)) {
        const breakdown = item.extraction_source?.sku_report_breakdown;
        const isKnownSku = knownSkuSet.has(sku.toLowerCase().trim());
        groups.set(sku, {
          sku,
          description: item.description,
          isMatched: !!breakdown || isKnownSku,
          breakdown: breakdown as SkuGroup["breakdown"],
          lineItemIds: [],
        });
      }
      groups.get(sku)!.lineItemIds.push(item.id);
    });
    return Array.from(groups.values());
  }, [lineItems, knownSkuSet]);

  const matchedSkus = skuGroups.filter((g) => g.isMatched);
  const unmatchedSkus = skuGroups.filter((g) => !g.isMatched);
  const allMatched = unmatchedSkus.length === 0;

  const [isOpen, setIsOpen] = useState(!allMatched);

  // Reset state when PO changes
  useEffect(() => {
    setMappingChanges({});
    setIsOpen(!allMatched);
  }, [purchaseOrderId, allMatched]);

  // Build the list of SKUs available for the combobox
  // Include existing primary SKUs + matched SKUs from this PO
  const comboboxSkus = useMemo(() => {
    // Use a Map keyed by lowercase to deduplicate case-insensitively
    const skuMap = new Map<string, string>();
    availableSkus.forEach((s) => {
      const key = s.toLowerCase().trim();
      if (!skuMap.has(key)) skuMap.set(key, s);
    });
    matchedSkus.forEach((g) => {
      const key = g.sku.toLowerCase().trim();
      if (!skuMap.has(key)) skuMap.set(key, g.sku);
    });
    // Remove alias values so only primary values appear in the dropdown
    existingMappings?.forEach((m) => {
      skuMap.delete(m.alias_value.toLowerCase().trim());
    });
    return Array.from(skuMap.values()).sort();
  }, [availableSkus, matchedSkus, existingMappings]);

  // Build a lookup of existing alias -> primary mappings
  const existingMappingLookup = useMemo(() => {
    const lookup = new Map<string, string>();
    existingMappings?.forEach((m) => {
      lookup.set(m.alias_value.toLowerCase().trim(), m.primary_value);
    });
    return lookup;
  }, [existingMappings]);

  // Build reverse lookup: primary -> list of aliases
  const primaryToAliasesLookup = useMemo(() => {
    const lookup = new Map<string, string[]>();
    existingMappings?.forEach((m) => {
      const key = m.primary_value.toLowerCase().trim();
      if (!lookup.has(key)) lookup.set(key, []);
      lookup.get(key)!.push(m.alias_value);
    });
    return lookup;
  }, [existingMappings]);

  const hasChanges = Object.values(mappingChanges).some((v) => v !== null);

  const handleSaveMappings = async () => {
    setIsSaving(true);
    try {
      // Build set of existing alias mappings to avoid duplicates
      const existingAliases = new Set(
        existingMappings?.map((m) => m.alias_value.toLowerCase().trim()) ?? []
      );

      // Insert new mappings
      const newMappings = Object.entries(mappingChanges).filter(
        ([aliasSku, primarySku]) =>
          primarySku !== null && !existingAliases.has(aliasSku.toLowerCase().trim())
      );

      for (const [aliasSku, primarySku] of newMappings) {
        if (!primarySku) continue;
        await createMapping.mutateAsync({
          organisationId,
          primarySku,
          aliasSku,
          createdByEmail: userEmail,
          productIdentifier,
        });
      }

      // Trigger recalculation via edge function
      const { error: calcError } = await invokeFunction(supabase, 
        "calculate-po-funding",
        {
          body: { purchaseOrderId, organisationId },
        }
      );

      if (calcError) {
        console.error("Recalculation error:", calcError);
        toast({
          title: "Mappings saved",
          description: "Mappings were saved but recalculation encountered an error. Values may update shortly.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Mappings saved",
          description: `${newMappings.length} mapping(s) saved. Financial values have been recalculated.`,
        });
      }

      // Refresh line items data
      queryClient.invalidateQueries({ queryKey: ["po-line-items", purchaseOrderId] });
      setMappingChanges({});
    } catch (err) {
      console.error("Save mappings error:", err);
      toast({
        title: "Error saving mappings",
        description: "Failed to save SKU mappings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateProductWithType = async (result: CreateProductDialogResult) => {
    const sku = createDialogData?.sku;
    if (!sku) return;
    setCreatingProduct(sku);
    try {
      const typeMatchesOrg = result.identifierType === productIdentifier;
      const productSku = typeMatchesOrg
        ? sku
        : result.primaryValue ?? sku;

      // Create product — if it already exists, continue to mapping step
      let productAlreadyExisted = false;
      try {
        await createProduct.mutateAsync({
          organisationId,
          product: productSku,
          description: result.description,
          createdByEmail: userEmail,
          source: "po_mapping_review",
          identifierType: result.identifierType,
        });
      } catch (productErr: unknown) {
        const isDuplicate =
          productErr instanceof Error &&
          (productErr.message.includes("duplicate") || productErr.message.includes("already exists"));
        if (!typeMatchesOrg && isDuplicate) {
          productAlreadyExisted = true;
        } else {
          throw productErr;
        }
      }

      // Case B or C: identifier type differs from org primary — create a mapping
      if (!typeMatchesOrg) {
        await createMapping.mutateAsync({
          organisationId,
          primarySku: productSku,
          aliasSku: sku,
          createdByEmail: userEmail,
          productIdentifier,
          aliasType: result.identifierType,
        });
      }

      // Trigger recalculation
      const { error: calcError } = await invokeFunction(supabase, 
        "calculate-po-funding",
        { body: { purchaseOrderId, organisationId } }
      );

      if (calcError) {
        console.error("Recalculation error:", calcError);
      }

      toast({
        title: productAlreadyExisted ? "Mapping created" : "Product created",
        description: productAlreadyExisted
          ? `"${sku}" has been mapped to existing product "${productSku}".`
          : `"${productSku}" has been created as a new product.`,
      });

      setCreateDialogData(null);
      queryClient.invalidateQueries({ queryKey: ["po-line-items", purchaseOrderId] });
    } catch (err) {
      console.error("Create product error:", err);
      toast({
        title: "Error creating product",
        description: "Failed to create product or mapping. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCreatingProduct(null);
    }
  };

  if (skuGroups.length === 0) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CardHeader className="py-3 px-4">
          <CollapsibleTrigger asChild>
            <button className="flex items-center justify-between w-full text-left">
              <div className="flex items-center gap-2">
                <Link className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">
                  Product Mapping Review
                </CardTitle>
                {allMatched ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    All {matchedSkus.length} products matched
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {matchedSkus.length} of {skuGroups.length} products matched
                  </Badge>
                )}
              </div>
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          </CollapsibleTrigger>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="px-4 pb-4 pt-0 space-y-4">
            {/* Source Filter */}
            {derivedSourceOptions.length > 1 && (
              <div className="flex items-center gap-2">
                <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Product Source:</span>
                <Select value={selectedSourceType} onValueChange={setSelectedSourceType}>
                  <SelectTrigger className="w-[180px] h-8 text-xs">
                    <SelectValue placeholder="All Sources" />
                  </SelectTrigger>
                  <SelectContent>
                    {derivedSourceOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-xs">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Unmatched SKUs */}
            {unmatchedSkus.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                  Unmatched Products ({unmatchedSkus.length})
                </h4>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        <th className="text-left px-3 py-2 font-medium">Extracted Identifier</th>
                        <th className="text-left px-3 py-2 font-medium">Description</th>
                        <th className="text-left px-3 py-2 font-medium">Matched Product</th>
                        <th className="text-left px-3 py-2 font-medium">Or</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unmatchedSkus.map((group) => (
                        <tr key={group.sku} className="border-b last:border-0 bg-amber-50/30">
                          <td className="px-3 py-2 font-mono">
                            <div className="flex items-center gap-1">
                              <span>{group.sku}</span>
                              {mappingChanges[group.sku] && (
                                <>
                                  <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                                  <span className="text-xs text-muted-foreground">{mappingChanges[group.sku]}</span>
                                </>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground truncate max-w-[200px]">
                            {group.description || "—"}
                          </td>
                          <td className="px-3 py-2">
                            <SkuCombobox
                              availableSkus={comboboxSkus}
                              value={mappingChanges[group.sku] ?? null}
                              onChange={(val) =>
                                setMappingChanges((prev) => ({
                                  ...prev,
                                  [group.sku]: val,
                                }))
                              }
                              placeholder="Select Product..."
                              currentSku={group.sku}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs h-8 whitespace-nowrap"
                              disabled={creatingProduct === group.sku || !!mappingChanges[group.sku] || orgConnections === null}
                              onClick={() => setCreateDialogData({ sku: group.sku, description: group.description })}
                            >
                              {creatingProduct === group.sku ? (
                                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              ) : (
                                <Plus className="h-3 w-3 mr-1" />
                              )}
                              Create New
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Matched SKUs */}
            {matchedSkus.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                  Matched Products ({matchedSkus.length})
                </h4>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        <th className="text-left px-3 py-2 font-medium">Product</th>
                        <th className="text-right px-3 py-2 font-medium">Inventory</th>
                        <th className="text-right px-3 py-2 font-medium">12mo Sales</th>
                        <th className="text-left px-3 py-2 font-medium">Remap</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matchedSkus.map((group) => (
                        <tr key={group.sku} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono">
                            <div className="flex items-center gap-1">
                              <CheckCircle className="h-3 w-3 text-green-600 shrink-0" />
                              <span>{group.sku}</span>
                              {mappingChanges[group.sku] ? (
                                <>
                                  <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                                  <span className="text-xs text-muted-foreground">{mappingChanges[group.sku]}</span>
                                </>
                              ) : existingMappingLookup.get(group.sku.toLowerCase().trim()) && existingMappingLookup.get(group.sku.toLowerCase().trim())!.toLowerCase().trim() !== group.sku.toLowerCase().trim() ? (
                                <>
                                  <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                                  <span className="text-xs text-muted-foreground">
                                    (alias of {existingMappingLookup.get(group.sku.toLowerCase().trim())})
                                  </span>
                                </>
                              ) : primaryToAliasesLookup.get(group.sku.toLowerCase().trim())?.filter(a => a.toLowerCase().trim() !== group.sku.toLowerCase().trim()).length ? (
                                <span className="text-xs text-muted-foreground">
                                  (aliases: {primaryToAliasesLookup.get(group.sku.toLowerCase().trim())!.filter(a => a.toLowerCase().trim() !== group.sku.toLowerCase().trim()).join(", ")})
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {group.breakdown?.stock_held?.toLocaleString() ?? "—"}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {group.breakdown?.units_sold?.toLocaleString() ?? "—"}
                          </td>
                          <td className="px-3 py-2">
                            <SkuCombobox
                              availableSkus={comboboxSkus}
                              value={mappingChanges[group.sku] ?? null}
                              onChange={(val) =>
                                setMappingChanges((prev) => ({
                                  ...prev,
                                  [group.sku]: val,
                                }))
                              }
                              placeholder="Change..."
                              currentSku={group.sku}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Save Button */}
            {hasChanges && (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={handleSaveMappings}
                  disabled={isSaving}
                >
                  {isSaving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                  Save Mappings & Recalculate
                </Button>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>

      <CreateProductDialog
        open={createDialogData !== null}
        onOpenChange={(open) => {
          if (!open) setCreateDialogData(null);
        }}
        extractedIdentifier={createDialogData?.sku ?? ""}
        extractedDescription={createDialogData?.description ?? null}
        orgProductIdentifier={productIdentifier}
        onConfirm={handleCreateProductWithType}
        isCreating={creatingProduct !== null}
      />
    </Collapsible>
  );
}
